/**
 * The sidebar's information architecture — data, not markup — so its shape is unit-testable without
 * rendering (the page-titles.ts pattern). `components/app-sidebar.tsx` renders it; nav.test.ts guards it.
 *
 * Seven top-level rows plus a Settings group pinned to the bottom (docs/sidebar-nav-audit-and-plan-v1.md
 * §3). The groups are channel-neutral on purpose: "Orders" is intake, not "Shopify", so quotes, leads
 * and calls slot in under it later without renaming anything above them; "Production" holds every lane
 * the board itself knows about; "Stations" mirrors the benches on the floor.
 */
import {
  Activity,
  Boxes,
  ClipboardList,
  Factory,
  Printer,
  Settings,
  Warehouse,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import type { Role } from '@/lib/roles';

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

/** A row that navigates. A group's children have no icon of their own — the group carries it. */
export type NavLeaf = {
  label: string;
  to: string;
  /** Highlight on any nested path under `to` (the Inventory hub's tabs), not just an exact match. */
  matchPrefix?: boolean;
  /**
   * A full-bleed station that renders WITHOUT the shell — click it and there is no sidebar to come
   * back to (router.tsx). Rendered with a marker + `KIOSK_TOOLTIP` so the row says so up front.
   */
  kiosk?: boolean;
  /**
   * Cosmetic visibility (roles.ts doctrine: the server is the gate, this only hides a door). Absent =
   * everyone. An unknown role (loading / signed-out) sees only ungated rows.
   */
  visibleTo?: (role: Role) => boolean;
};

export type NavGroup = {
  label: string;
  icon: NavIcon;
  children: NavLeaf[];
};

export type NavEntry = ({ kind: 'leaf'; icon: NavIcon } & NavLeaf) | ({ kind: 'group' } & NavGroup);

export const NAV: readonly NavEntry[] = [
  { kind: 'leaf', label: 'Orders', icon: ClipboardList, to: '/orders' },
  {
    kind: 'group',
    label: 'Production',
    icon: Factory,
    children: [
      { label: 'Board', to: '/production' },
      // A selected sheet lives at /batches/:batchId (the workbench), so the row matches the prefix.
      { label: 'Gang sheets', to: '/batches', matchPrefix: true },
      // A filmless screen-print RUN is the same batch record but a different object on the floor —
      // screens, not film — so it gets its own row rather than a tab on a page titled "Gang sheets".
      // Same reasoning that earned Caps and Ship-only theirs.
      { label: 'Screen print', to: '/screen-print' },
      // The queue is where a reprint waits, the sheet is what it becomes — adjacent on purpose.
      { label: 'Reprint queue', to: '/reprint-queue' },
      // Production LANES in the system's own vocabulary (NON_DTF, FULFILLMENT_ONLY): made or packed by
      // the shop but off the gang sheet, so they never appear in the DTF queue. Labels are canon in the
      // order card strip and in tests — the group, not a rename, resolves the "Ship" collision.
      { label: 'Caps', to: '/caps' },
      { label: 'Ship-only', to: '/fulfill' },
    ],
  },
  // Already a hub with its own tabs, KPI strip and badges — the model, not a candidate for sub-rows.
  { kind: 'leaf', label: 'Inventory', icon: Warehouse, to: '/inventory', matchPrefix: true },
  {
    kind: 'group',
    label: 'Catalog',
    icon: Boxes,
    children: [
      { label: 'Designs', to: '/designs' },
      // The page title stays "Product catalog" (page-titles.ts); under a group called Catalog the row
      // only needs the noun.
      { label: 'Products', to: '/catalog' },
    ],
  },
  {
    kind: 'group',
    label: 'Stations',
    icon: Printer,
    children: [
      { label: 'Press', to: '/press' },
      { label: 'Scan', to: '/scan', kiosk: true },
      { label: 'Ship', to: '/ship', kiosk: true },
      // A phone task at the shelf; also linked from the Inventory hub header.
      { label: 'Count the shelf', to: '/count' },
    ],
  },
  { kind: 'leaf', label: 'Activity', icon: Activity, to: '/activity' },
];

/** Pinned to the bottom of the sidebar; collapsed unless the current page is one of its rows. */
export const SETTINGS: NavGroup = {
  label: 'Settings',
  icon: Settings,
  children: [
    { label: 'Stores', to: '/settings/stores' },
    // Reorder model · ship-to address · S&S payment method — the dialogs the Inventory pages open, given
    // an address of their own so the ship station's refusal can name a place (audit F5).
    { label: 'Purchasing', to: '/settings/purchasing' },
    { label: 'Film rolls', to: '/settings/film-rolls' },
    // The screen-printing presses — stations · heads · flash stations (screen-print station P2).
    { label: 'Presses', to: '/settings/presses' },
    {
      label: 'Pricing',
      to: '/settings/pricing',
      visibleTo: (role) => role.isPricingAdmin || role.isAdmin,
    },
  ],
};

/**
 * Shell routes that exist but are deliberately NOT in the nav, each with its reason. The nav guard
 * (nav.test.ts) requires every titled route to be reachable from the sidebar OR listed here, so a route
 * cannot silently become an orphan again (audit F9 — `/quote/screen-print` had been one for two months).
 */
export const HIDDEN_ROUTES: Readonly<Record<string, string>> = {
  '/quote/screen-print':
    'Quote calculator paused by the owner 2026-07-16 (3f6b256); v16 is the day-to-day tool.',
};

export const KIOSK_TOOLTIP = 'Opens full screen on this device';

export function isActivePath(item: Pick<NavLeaf, 'to' | 'matchPrefix'>, pathname: string): boolean {
  return item.matchPrefix
    ? pathname === item.to || pathname.startsWith(`${item.to}/`)
    : item.to === pathname;
}

/** The rows of a group this role may see. */
export function visibleChildren(group: NavGroup, role: Role | undefined): NavLeaf[] {
  return group.children.filter((c) => !c.visibleTo || (role !== undefined && c.visibleTo(role)));
}

/** Every navigable row — top-level leaves, group children and Settings — regardless of role. */
export function navLeaves(): NavLeaf[] {
  const leaves: NavLeaf[] = [];
  for (const entry of NAV) {
    if (entry.kind === 'leaf') leaves.push(entry);
    else leaves.push(...entry.children);
  }
  leaves.push(...SETTINGS.children);
  return leaves;
}

/** The label of the group whose row is the current page, or undefined for a top-level page. */
export function groupForPath(pathname: string): string | undefined {
  const groups: NavGroup[] = [];
  for (const entry of NAV) if (entry.kind === 'group') groups.push(entry);
  groups.push(SETTINGS);
  return groups.find((g) => g.children.some((c) => isActivePath(c, pathname)))?.label;
}
